import { QueryFunctionContext, useQuery } from 'react-query'
import { castImmutable } from 'immer'
import apiClient from '../../utils/api'
import { TFeatureFlagKey, TFeatureFlags } from '../../utils/types'
import {
    OptimisticTransaction,
    beginOptimisticTransaction,
    optimisticQueryChange,
    reconcileOptimisticTransaction,
    rollbackOptimisticTransaction,
} from '../optimisticTransaction'
import { useGTMutation, useGTQueryClient } from '../queryUtils'

export const useFeatureFlags = () =>
    useQuery<TFeatureFlags>('feature_flags', getFeatureFlags, {
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
    })

// The backend deploys separately. Versions from before these flags omit them, and both features
// were ungated then, so missing values mean on rather than hiding them from everyone.
const normalizeFeatureFlags = (data: Partial<TFeatureFlags>) =>
    castImmutable({
        ...data,
        goals: data.goals ?? true,
        research: data.research ?? true,
    }) as TFeatureFlags

const getFeatureFlags = async ({ signal }: QueryFunctionContext) => {
    const response = await apiClient.get('/feature_flags/', { signal })
    return normalizeFeatureFlags(response.data)
}

/** False until the flags load, so gated queries never fire for a user the feature is off for. */
export const useIsFeatureEnabled = (flag: TFeatureFlagKey) => Boolean(useFeatureFlags().data?.[flag])

/** Opts the signed-in user in to (true) or out of (false) any subset of feature flags. */
export const useUpdateFeatureFlagOptIns = () => {
    const queryClient = useGTQueryClient()
    return useGTMutation<
        TFeatureFlags,
        unknown,
        Partial<Record<TFeatureFlagKey, boolean>>,
        OptimisticTransaction<TFeatureFlags>
    >(
        async (optIns) => {
            const response = await apiClient.patch('/feature_flags/', optIns)
            return normalizeFeatureFlags(response.data)
        },
        {
            tag: 'feature_flags',
            errorMessage: 'update experimental features',
            invalidateTagsOnSettled: ['feature_flags', 'overview', 'overview-supported-views'],
            onMutate: (optIns) => {
                void queryClient.cancelQueries('feature_flags')
                return beginOptimisticTransaction(queryClient, [
                    optimisticQueryChange<TFeatureFlags, TFeatureFlags>(
                        'feature_flags',
                        (current) => {
                            if (!current) return current
                            const next = { ...current, flags: current.flags.map((flag) => ({ ...flag })) }
                            for (const [untypedKey, optedIn] of Object.entries(optIns)) {
                                const key = untypedKey as TFeatureFlagKey
                                if (optedIn === undefined) continue
                                const flag = next.flags.find((candidate) => candidate.key === key)
                                if (!flag) continue
                                flag.opted_in = optedIn
                                flag.enabled = flag.globally_enabled || optedIn
                                next[key] = flag.enabled
                            }
                            return next
                        },
                        { reconcile: (_current, data) => data }
                    ),
                ])
            },
            onSuccess: (data, _optIns, transaction) => reconcileOptimisticTransaction(transaction, data),
            onError: (_error, _optIns, transaction) => rollbackOptimisticTransaction(transaction),
        },
        false
    )
}
